import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function TrackerResultPage({ success }) {
  const [params] = useSearchParams();
  const [state, setState] = useState({ loading: success, status: null });

  useEffect(() => {
    if (!success) return;
    const sid = params.get("session_id");
    if (!sid) {
      setState({ loading: false, status: null });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await apiClient.get(`/tracker/status/${sid}`);
        if (data.payment_status === "paid") {
          setState({ loading: false, status: data });
          return;
        }
      } catch {
        /* ignore transient */
      }
      attempts += 1;
      if (attempts < 10) setTimeout(poll, 2000);
      else setState({ loading: false, status: null });
    };
    poll();
  }, [params, success]);

  return (
    <div className="mx-auto max-w-xl px-4 sm:px-6 py-16" data-testid="tracker-result-page">
      <Card className="p-6 border-slate-200 text-center">
        {success ? (
          state.loading ? (
            <>
              <Loader2 size={32} className="mx-auto animate-spin text-blue-600" />
              <h1 className="mt-4 font-heading text-xl font-bold text-slate-900">Confirming your subscription…</h1>
              <p className="mt-2 text-sm text-slate-600">Checking with Stripe. This usually takes a few seconds.</p>
            </>
          ) : state.status ? (
            <>
              <CheckCircle2 size={32} className="mx-auto text-emerald-600" />
              <h1 className="mt-4 font-heading text-xl font-bold text-slate-900">Tracking is active</h1>
              <p className="mt-2 text-sm text-slate-700">
                You'll receive email alerts for <strong>{state.status.target_label}</strong>.
              </p>
              <Link to="/" className="mt-4 inline-block text-blue-700 hover:underline">← Back to search</Link>
            </>
          ) : (
            <>
              <Loader2 size={32} className="mx-auto text-slate-400" />
              <h1 className="mt-4 font-heading text-xl font-bold text-slate-900">Processing…</h1>
              <p className="mt-2 text-sm text-slate-600">
                Payment confirmation is taking longer than expected. You'll receive an email once it completes.
              </p>
              <Link to="/" className="mt-4 inline-block text-blue-700 hover:underline">← Back to search</Link>
            </>
          )
        ) : (
          <>
            <XCircle size={32} className="mx-auto text-slate-400" />
            <h1 className="mt-4 font-heading text-xl font-bold text-slate-900">Checkout cancelled</h1>
            <p className="mt-2 text-sm text-slate-600">No charge was made. You can subscribe at any time.</p>
            <Link to="/" className="mt-4 inline-block text-blue-700 hover:underline">← Back to search</Link>
          </>
        )}
      </Card>
    </div>
  );
}
